import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Loader2, UserCog, Search, Mail, Phone, ChevronRight, DollarSign,
  CheckCircle2, XCircle, Plus, Pencil, Save, Link2, ExternalLink, RotateCcw,
  Camera, Trash2, AlertTriangle, Sparkles, CalendarClock, ClipboardList
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import UserAvatar from '../../components/UserAvatar';
import RecordActions from '../../components/RecordActions';
import GuruTasksDialog from '../../components/admin/GuruTasksDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache-busting helper — the /guru-photos endpoint sends `Cache-Control:
// public, max-age=300` so we append a signature that only changes when the
// photo path changes on the profile.
const guruPhotoSrc = (userId, photoUrl) => {
  if (!userId || !photoUrl) return null;
  const suffix = photoUrl.split('/').pop() || '';
  return `${API_URL}/api/guru-photos/${userId}?v=${encodeURIComponent(suffix)}`;
};

const AdminCleanersPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [workload, setWorkload] = useState({});
  const [workerFilter, setWorkerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active | archived | all

  // Detail/Edit dialog
  const [selectedCleaner, setSelectedCleaner] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', full_name: '', phone: '', display_name: '', worker_type: '1099', pay_structure: 'hourly', hourly_rate: '', commission_rate: '' });

  // Stripe Connect
  const [stripeLoading, setStripeLoading] = useState(false);
  const [tasksDialogGuru, setTasksDialogGuru] = useState(null);

  // Qualified services master list (Session 3)
  const [qualifiedServices, setQualifiedServices] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${API_URL}/api/gurus/qualified-services`, { headers })
      .then(r => setQualifiedServices(r.data.services || []))
      .catch(() => setQualifiedServices([]));
  }, [headers]);

  const fetchCleaners = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (workerFilter !== 'all') params.set('worker_type', workerFilter);
      if (statusFilter !== 'active') params.set('show_archived', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API_URL}/api/cleaners${qs}`, { headers });
      setCleaners(res.data);
    } catch { toast.error('Failed to load cleaners'); }
    finally { setLoading(false); }
  }, [headers, workerFilter, statusFilter]);

  useEffect(() => { fetchCleaners(); }, [fetchCleaners]);
  useEffect(() => { axios.get(`${API_URL}/api/admin/schedule/team-workload`, { headers }).then((r) => setWorkload(r.data)).catch(() => {}); }, [headers]);

  const openDetail = (c) => {
    setSelectedCleaner(c);
    setEditing(false);
    setEditForm({
      display_name: c.display_name || '',
      bio: c.bio || '',
      worker_type: c.worker_type,
      pay_structure: c.pay_structure,
      hourly_rate: c.hourly_rate != null ? String(c.hourly_rate) : '',
      commission_rate: c.commission_rate != null ? String(c.commission_rate) : '',
      is_available: c.is_available,
      payouts_enabled: c.payouts_enabled,
      payout_minimum_cents: String(c.payout_minimum_cents || 5000),
      qualified_services: c.qualified_services || [],
    });
    setDetailOpen(true);
  };

  const saveEdit = async () => {
    if (!selectedCleaner) return;
    setSaving(true);
    try {
      const payload = {
        display_name: editForm.display_name || undefined,
        // Always send bio as a string (empty === clear). Backend accepts up
        // to 200 chars; empty string is a valid clear signal.
        bio: (editForm.bio || '').trim(),
        worker_type: editForm.worker_type,
        pay_structure: editForm.pay_structure,
        hourly_rate: editForm.hourly_rate ? parseFloat(editForm.hourly_rate) : null,
        commission_rate: editForm.commission_rate ? parseFloat(editForm.commission_rate) : null,
        is_available: editForm.is_available,
        payouts_enabled: editForm.payouts_enabled,
        payout_minimum_cents: parseInt(editForm.payout_minimum_cents) || 5000,
        qualified_services: editForm.qualified_services || [],
      };
      await axios.put(`${API_URL}/api/cleaners/${selectedCleaner.user_id}`, payload, { headers });
      toast.success('Guru updated');
      setDetailOpen(false);
      fetchCleaners();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const createCleaner = async () => {
    if (!createForm.email || !createForm.password || !createForm.full_name) { toast.error('Email, password, and name required'); return; }
    setSaving(true);
    try {
      const params = new URLSearchParams({
        email: createForm.email, password: createForm.password, full_name: createForm.full_name,
        worker_type: createForm.worker_type, pay_structure: createForm.pay_structure,
      });
      if (createForm.phone) params.append('phone', createForm.phone);
      if (createForm.display_name) params.append('display_name', createForm.display_name);
      if (createForm.hourly_rate) params.append('hourly_rate', createForm.hourly_rate);
      if (createForm.commission_rate) params.append('commission_rate', createForm.commission_rate);
      await axios.post(`${API_URL}/api/cleaners?${params.toString()}`, {}, { headers });
      toast.success('Guru created');
      setCreateOpen(false);
      setCreateForm({ email: '', password: '', full_name: '', phone: '', display_name: '', worker_type: '1099', pay_structure: 'hourly', hourly_rate: '', commission_rate: '' });
      fetchCleaners();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const startStripeOnboarding = async (cleaner) => {
    setStripeLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/cleaners/${cleaner.user_id}/stripe-onboard`,
        null,
        { headers, params: { return_url: `${window.location.origin}/admin/cleaners`, refresh_url: `${window.location.origin}/admin/cleaners` } }
      );
      if (res.data.onboarding_url) {
        window.open(res.data.onboarding_url, '_blank');
        toast.success('Stripe onboarding link opened');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start onboarding'); }
    finally { setStripeLoading(false); }
  };

  const archiveCleaner = async (cleaner) => {
    if (!window.confirm(`Archive ${cleaner.user_name || cleaner.display_name}? They'll be hidden from default listings and marked unavailable.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/cleaners/${cleaner.user_id}/archive`, {}, { headers });
      toast.success('Guru archived');
      fetchCleaners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    }
  };

  const restoreCleaner = async (cleaner) => {
    try {
      await axios.post(`${API_URL}/api/admin/cleaners/${cleaner.user_id}/restore`, {}, { headers });
      toast.success('Guru restored');
      fetchCleaners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
  };

  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedCleaner) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5 MB'); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API_URL}/api/cleaners/${selectedCleaner.user_id}/photo`, fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      // Update local state so the preview refreshes immediately
      const updated = { ...selectedCleaner, photo_url: res.data.photo_url };
      setSelectedCleaner(updated);
      setCleaners(prev => prev.map(c => c.user_id === updated.user_id ? { ...c, photo_url: res.data.photo_url } : c));
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally { setPhotoUploading(false); }
  };

  const handlePhotoClear = async () => {
    if (!selectedCleaner) return;
    if (!window.confirm('Remove this Guru\u2019s photo?')) return;
    try {
      await axios.delete(`${API_URL}/api/cleaners/${selectedCleaner.user_id}/photo`, { headers });
      setSelectedCleaner({ ...selectedCleaner, photo_url: null });
      setCleaners(prev => prev.map(c => c.user_id === selectedCleaner.user_id ? { ...c, photo_url: null } : c));
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const toggleQualification = (svc) => {
    setEditForm(f => {
      const list = f.qualified_services || [];
      return { ...f, qualified_services: list.includes(svc) ? list.filter(s => s !== svc) : [...list, svc] };
    });
  };

  const filtered = cleaners.filter(c => {
    const cstatus = c.status || 'active';
    if (statusFilter === 'active' && cstatus !== 'active') return false;
    if (statusFilter === 'archived' && cstatus !== 'archived') return false;
    if (!search) return true;
    const t = search.toLowerCase();
    return (c.user_name || '').toLowerCase().includes(t) || (c.display_name || '').toLowerCase().includes(t) || (c.user_email || '').toLowerCase().includes(t);
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>;

  return (
    <div className="min-h-screen" data-testid="admin-cleaners-page">
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-[#77776e] flex-1 min-w-[200px]">Profiles, assignments, and workload.</p>
          <Button size="sm" className="bg-[#f36a19] hover:bg-[#C2410C] text-[#171912] font-bold" onClick={() => setCreateOpen(true)} data-testid="create-cleaner-btn"><Plus className="h-4 w-4 mr-1" />Add Guru</Button>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" /><Input placeholder="Search gurus..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white" data-testid="search-input" /></div>
          <Select value={workerFilter} onValueChange={setWorkerFilter}><SelectTrigger className="w-[140px] bg-white" data-testid="worker-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="1099">1099</SelectItem><SelectItem value="W2">W2</SelectItem></SelectContent></Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-[140px] bg-white" data-testid="status-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
        </div>
        {filtered.length === 0 ? (
          <div className="rounded-[10px] border border-[#e7e4db] bg-[#fffefa] p-12 text-center"><UserCog className="h-12 w-12 text-zinc-300 mx-auto mb-3" /><p className="text-zinc-500">No gurus found</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="guru-grid">
            {filtered.map((c, i) => {
              const isArchived = c.status === 'archived';
              const photoSrc = guruPhotoSrc(c.user_id, c.photo_url);
              const eligibilityReasons = [];
              if (!c.photo_url) eligibilityReasons.push('No photo');
              if (!(c.qualified_services || []).length) eligibilityReasons.push('No services');
              const w = workload[c.user_id] || { upcoming: 0, open_tasks: 0 };
              return (
              <motion.article key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className={`rounded-[10px] border border-[#e7e4db] bg-[#fffefa] p-5 flex flex-col ${isArchived ? 'opacity-70' : ''}`} data-testid={`cleaner-${c.id}`}>
                <div className="flex items-start justify-between">
                  <UserAvatar user={{ full_name: c.user_name || c.display_name, photo_url: photoSrc || c.user_photo_url, role: 'cleaner' }} size="lg" testId={`cleaner-avatar-${c.id}`} />
                  <div className="flex flex-wrap justify-end gap-1">
                    {isArchived ? <Badge variant="outline" className="bg-zinc-200 text-zinc-700 border-zinc-300 text-[10px]" data-testid={`cleaner-archived-${c.user_id}`}>Archived</Badge>
                      : c.is_available ? <Badge variant="outline" className="bg-[#eef3e8] text-[#55704a] border-[#d6e0cb] text-[10px]">Available</Badge> : <Badge variant="outline" className="bg-zinc-100 text-zinc-500 border-zinc-200 text-[10px]">Unavailable</Badge>}
                    {!c.is_eligible_for_assignment && !isArchived && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] flex items-center gap-1" data-testid={`cleaner-ineligible-${c.user_id}`} title={eligibilityReasons.join(' · ')}>
                        <AlertTriangle className="h-3 w-3" /> Setup incomplete
                      </Badge>
                    )}
                    {c.stripe_connected_account_id ? <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">Stripe</Badge> : null}
                  </div>
                </div>
                <h2 className="text-xl font-medium tracking-tight text-[#20211d] mt-4">{c.user_name || c.display_name}</h2>
                <p className="text-xs text-[#77776e] truncate">{c.user_email} · {c.worker_type} · {c.pay_structure}</p>
                {(c.qualified_services || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`cleaner-quals-${c.user_id}`}>
                    {(c.qualified_services || []).slice(0, 3).map(s => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-[#efeee6] text-[#5f6455]">{s}</span>)}
                    {(c.qualified_services || []).length > 3 && <span className="text-[10px] px-1.5 py-0.5 text-zinc-500">+{(c.qualified_services || []).length - 3} more</span>}
                  </div>
                )}
                <div className="grid grid-cols-3 mt-4 rounded-md border border-[#ecebe4] divide-x divide-[#ecebe4]" data-testid={`cleaner-workload-${c.user_id}`}>
                  <div className="p-3"><strong className="block text-2xl font-medium">{c.today_appointment_count || 0}</strong><small className="text-[10px] text-[#77776e]">Today</small></div>
                  <div className="p-3"><strong className="block text-2xl font-medium" data-testid={`cleaner-upcoming-${c.user_id}`}>{w.upcoming}</strong><small className="text-[10px] text-[#77776e]">Upcoming cleans</small></div>
                  <div className="p-3"><strong className="block text-2xl font-medium" data-testid={`cleaner-open-tasks-${c.user_id}`}>{w.open_tasks}</strong><small className="text-[10px] text-[#77776e]">Open tasks</small></div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-1">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openDetail(c)} data-testid={`cleaner-open-${c.user_id}`}>Open profile ↗</Button>
                  <Link to={`/admin/calendar?guru=${c.user_id}`} className="text-xs font-semibold text-[#b75b28]" data-testid={`cleaner-availability-${c.user_id}`}>View availability</Link>
                  <span className="flex-1" />
                  {isArchived ? (
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => restoreCleaner(c)} data-testid={`cleaner-restore-${c.user_id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setTasksDialogGuru({ id: c.user_id, full_name: c.user_name || c.display_name })} data-testid={`cleaner-tasks-btn-${c.user_id}`} title="Manage this Guru's tasks"><ClipboardList className="h-3.5 w-3.5 mr-1" /> Tasks</Button>
                      <RecordActions canEdit={true} canArchive={true} canDelete={true} deleteUrl={`/api/admin/cleaners/${c.user_id}`}
                        deleteConfirmTitle={`Delete ${c.user_name || c.display_name}?`}
                        deleteConfirmDescription="Permanently removes the guru and their non-business records. If they have completed jobs, time entries, paid payouts, or Stripe linkage, deletion will be blocked — archive instead."
                        onEdit={() => openDetail(c)} onArchive={() => archiveCleaner(c)} onDeleted={fetchCleaners} headers={headers} testIdPrefix="cleaner" entityId={c.user_id} />
                    </>
                  )}
                </div>
              </motion.article>
              );
            })}
          </div>
        )}
        <section className="rounded-[10px] bg-[#252a20] text-white p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="team-hiring-callout">
          <div className="flex-1"><p className="text-[9px] tracking-[0.16em] font-bold text-[#f59b61]">ROOM TO GROW</p><h3 className="text-lg font-semibold mt-1">Keep the team and the hiring pipeline connected.</h3><p className="text-xs text-[#b5bca9] mt-1">Review applicants, interviews and trial cleans before onboarding a new Guru.</p></div>
          <Link to="/admin/hiring" className="text-xs border border-white/30 rounded-md px-4 py-2.5 hover:bg-white/10" data-testid="team-open-hiring">Open Hiring ↗</Link>
        </section>
      </main>

      {/* Detail / Edit Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedCleaner?.user_name || selectedCleaner?.display_name}</span>
              {!editing && <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="edit-cleaner-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
            </DialogTitle>
            <DialogDescription>Guru profile, pay details, and Stripe Connect</DialogDescription>
          </DialogHeader>
          {selectedCleaner && !editing && (
            <div className="space-y-4 mt-2">
              {/* Photo + eligibility snapshot */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                <div className="relative flex-shrink-0">
                  <UserAvatar
                    user={{
                      full_name: selectedCleaner.user_name || selectedCleaner.display_name,
                      photo_url: guruPhotoSrc(selectedCleaner.user_id, selectedCleaner.photo_url) || selectedCleaner.user_photo_url,
                      role: 'cleaner',
                    }}
                    size="lg"
                    testId="guru-photo-preview"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoUploading}
                    className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#F56F05] hover:bg-[#C2410C] text-white flex items-center justify-center shadow-md disabled:opacity-50"
                    aria-label="Upload photo"
                    data-testid="guru-photo-upload-btn"
                  >
                    {photoUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoPick} className="hidden" data-testid="guru-photo-input" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900">
                    {selectedCleaner.is_eligible_for_assignment ? (
                      <span className="text-emerald-700 inline-flex items-center gap-1" data-testid="guru-eligible-badge">
                        <CheckCircle2 className="h-4 w-4" /> Ready to assign
                      </span>
                    ) : (
                      <span className="text-amber-700 inline-flex items-center gap-1" data-testid="guru-ineligible-badge">
                        <AlertTriangle className="h-4 w-4" /> Setup incomplete
                      </span>
                    )}
                  </p>
                  <ul className="text-[11px] text-zinc-500 mt-1 space-y-0.5">
                    <li className="flex items-center gap-1">
                      {selectedCleaner.photo_url
                        ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        : <XCircle className="h-3 w-3 text-amber-500" />}
                      Profile photo {selectedCleaner.photo_url ? 'uploaded' : 'missing'}
                    </li>
                    <li className="flex items-center gap-1">
                      {(selectedCleaner.qualified_services || []).length > 0
                        ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        : <XCircle className="h-3 w-3 text-amber-500" />}
                      {(selectedCleaner.qualified_services || []).length} qualified service{(selectedCleaner.qualified_services || []).length === 1 ? '' : 's'}
                    </li>
                    <li className="flex items-center gap-1">
                      <CalendarClock className="h-3 w-3 text-zinc-400" />
                      {selectedCleaner.today_appointment_count || 0} appt{(selectedCleaner.today_appointment_count || 0) === 1 ? '' : 's'} today
                    </li>
                  </ul>
                  {selectedCleaner.photo_url && (
                    <Button size="sm" variant="ghost" className="h-6 text-[11px] text-red-600 mt-1 px-1"
                      onClick={handlePhotoClear} data-testid="guru-photo-clear-btn">
                      <Trash2 className="h-3 w-3 mr-1" /> Remove photo
                    </Button>
                  )}
                </div>
              </div>
              {selectedCleaner.bio && (
                <blockquote className="text-sm text-zinc-600 italic pl-3 border-l-2 border-[#F56F05]/40" data-testid="guru-bio-view">
                  &ldquo;{selectedCleaner.bio}&rdquo;
                </blockquote>
              )}
              <div className="flex items-center gap-2">
                {selectedCleaner.is_available ? <Badge className="bg-green-100 text-green-800">Available</Badge> : <Badge className="bg-zinc-100 text-zinc-600">Unavailable</Badge>}
                <Badge variant="outline">{selectedCleaner.worker_type}</Badge>
              </div>
              {(selectedCleaner.qualified_services || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-zinc-700 mb-1.5 flex items-center gap-1"><Sparkles className="h-4 w-4" />Qualified Services</h4>
                  <div className="flex flex-wrap gap-1.5" data-testid="guru-qualifications-view">
                    {(selectedCleaner.qualified_services || []).map(s => (
                      <Badge key={s} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-zinc-400" /><span>{selectedCleaner.user_email}</span></div>
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-zinc-400" /><span>{selectedCleaner.user_phone || 'N/A'}</span></div>
              </div>
              <Separator />
              <div>
                <h4 className="text-sm font-medium text-zinc-700 mb-2 flex items-center gap-1"><DollarSign className="h-4 w-4" />Pay Structure</h4>
                <div className="grid grid-cols-2 gap-3 text-sm bg-zinc-50 rounded-lg p-3">
                  <div><span className="text-zinc-400">Type</span><p className="font-medium">{selectedCleaner.pay_structure}</p></div>
                  {selectedCleaner.hourly_rate != null && <div><span className="text-zinc-400">Hourly Rate</span><p className="font-medium">${selectedCleaner.hourly_rate}/hr</p></div>}
                  {selectedCleaner.commission_rate != null && <div><span className="text-zinc-400">Commission</span><p className="font-medium">{selectedCleaner.commission_rate}%</p></div>}
                  <div><span className="text-zinc-400">Supplies</span><p className="font-medium capitalize">{selectedCleaner.supplies_provided_by}</p></div>
                  <div><span className="text-zinc-400">Min Payout</span><p className="font-medium">${(selectedCleaner.payout_minimum_cents / 100).toFixed(2)}</p></div>
                  <div><span className="text-zinc-400">Payouts</span><p className="font-medium">{selectedCleaner.payouts_enabled ? 'Enabled' : 'Disabled'}</p></div>
                </div>
              </div>
              <Separator />
              <div>
                <h4 className="text-sm font-medium text-zinc-700 mb-2 flex items-center gap-1"><CalendarClock className="h-4 w-4" />Job History</h4>
                <Button size="sm" variant="outline" className="w-full"
                  onClick={() => {
                    setDetailOpen(false);
                    navigate(`/admin/appointments?cleaner_id=${selectedCleaner.user_id}`);
                  }}
                  data-testid="guru-job-history-btn">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View every job with {selectedCleaner.user_name || selectedCleaner.display_name} on the team
                </Button>
              </div>
              <Separator />
              <div>
                <h4 className="text-sm font-medium text-zinc-700 mb-2 flex items-center gap-1"><Link2 className="h-4 w-4" />Stripe Connect</h4>
                {selectedCleaner.stripe_connected_account_id ? (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg p-3"><CheckCircle2 className="h-4 w-4" /><span>Connected: {selectedCleaner.stripe_connected_account_id}</span></div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-zinc-500 bg-zinc-50 rounded-lg p-3"><XCircle className="h-4 w-4" /><span>Not connected - send onboarding link to set up payouts</span></div>
                    {selectedCleaner.worker_type === '1099' && (
                      <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={() => startStripeOnboarding(selectedCleaner)} disabled={stripeLoading} data-testid="stripe-onboard-btn">
                        {stripeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}Start Stripe Onboarding
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {selectedCleaner && editing && (
            <div className="space-y-4 mt-2">
              <div><Label className="text-sm mb-1.5 block">Display Name</Label><Input value={editForm.display_name} onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))} data-testid="edit-display-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm mb-1.5 block">Worker Type</Label>
                  <Select value={editForm.worker_type} onValueChange={v => setEditForm(f => ({ ...f, worker_type: v }))}><SelectTrigger data-testid="edit-worker-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1099">1099</SelectItem><SelectItem value="W2">W2</SelectItem></SelectContent></Select>
                </div>
                <div><Label className="text-sm mb-1.5 block">Pay Structure</Label>
                  <Select value={editForm.pay_structure} onValueChange={v => setEditForm(f => ({ ...f, pay_structure: v }))}><SelectTrigger data-testid="edit-pay-structure"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="commission">Commission</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem></SelectContent></Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm mb-1.5 block">Hourly Rate ($)</Label><Input type="number" min="0" step="0.5" value={editForm.hourly_rate} onChange={e => setEditForm(f => ({ ...f, hourly_rate: e.target.value }))} data-testid="edit-hourly-rate" /></div>
                <div><Label className="text-sm mb-1.5 block">Commission (%)</Label><Input type="number" min="0" max="100" value={editForm.commission_rate} onChange={e => setEditForm(f => ({ ...f, commission_rate: e.target.value }))} data-testid="edit-commission-rate" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm mb-1.5 block">Min Payout (cents)</Label><Input type="number" min="0" value={editForm.payout_minimum_cents} onChange={e => setEditForm(f => ({ ...f, payout_minimum_cents: e.target.value }))} data-testid="edit-min-payout" /></div>
              </div>
              <div className="flex items-center justify-between"><Label>Available</Label><Switch checked={editForm.is_available} onCheckedChange={v => setEditForm(f => ({ ...f, is_available: v }))} data-testid="edit-available" /></div>
              <div className="flex items-center justify-between"><Label>Payouts Enabled</Label><Switch checked={editForm.payouts_enabled} onCheckedChange={v => setEditForm(f => ({ ...f, payouts_enabled: v }))} data-testid="edit-payouts-enabled" /></div>
              <Separator />
              <div>
                <Label className="text-sm mb-1.5 block">Client-facing Bio</Label>
                <p className="text-[11px] text-zinc-500 mb-1.5">Shown right beside this Guru&rsquo;s photo on the customer&rsquo;s booking confirmation email.</p>
                <Textarea
                  value={editForm.bio || ''}
                  onChange={e => setEditForm(f => ({ ...f, bio: e.target.value.slice(0, 200) }))}
                  placeholder="Hi, I'm Sean — Portland local, 4 years cleaning, and I never leave without a stocked bathroom."
                  className="min-h-[70px] resize-none"
                  data-testid="edit-bio-input"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[10px] text-zinc-400 italic">Kept short — 200 characters max keeps emails tidy.</p>
                  <span className={`text-[10px] font-mono ${(editForm.bio || '').length > 180 ? 'text-amber-600' : 'text-zinc-400'}`}
                    data-testid="edit-bio-counter">
                    {(editForm.bio || '').length}/200
                  </span>
                </div>
              </div>
              <Separator />
              <div>
                <Label className="text-sm mb-1.5 block flex items-center gap-1"><Sparkles className="h-4 w-4" />Qualified Services</Label>
                <p className="text-[11px] text-zinc-500 mb-2">Only services checked here will show this Guru as &ldquo;eligible&rdquo; in the Assigned Team picker.</p>
                <div className="grid grid-cols-2 gap-1.5" data-testid="edit-qualifications">
                  {qualifiedServices.map(svc => {
                    const on = (editForm.qualified_services || []).includes(svc);
                    return (
                      <button key={svc} type="button" onClick={() => toggleQualification(svc)}
                        className={`text-left text-xs px-2.5 py-1.5 rounded-md border transition-colors ${on ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
                        data-testid={`edit-qual-${svc.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                        <span className="inline-flex items-center gap-1.5">
                          {on ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="w-3.5 h-3.5 rounded border border-zinc-300" />}
                          {svc}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}>Cancel</Button>
                <Button className="flex-1 bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={saveEdit} disabled={saving} data-testid="save-cleaner-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Guru</DialogTitle>
            <DialogDescription>Create a new guru account and profile</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-sm mb-1.5 block">Full Name *</Label><Input value={createForm.full_name} onChange={e => setCreateForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Maria Garcia" data-testid="create-name" /></div>
            <div><Label className="text-sm mb-1.5 block">Email *</Label><Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="maria@example.com" data-testid="create-email" /></div>
            <div><Label className="text-sm mb-1.5 block">Password *</Label><Input type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} placeholder="Secure password" data-testid="create-password" /></div>
            <div><Label className="text-sm mb-1.5 block">Phone</Label><Input value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} placeholder="(555) 123-4567" data-testid="create-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm mb-1.5 block">Worker Type</Label>
                <Select value={createForm.worker_type} onValueChange={v => setCreateForm(f => ({ ...f, worker_type: v }))}><SelectTrigger data-testid="create-worker-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1099">1099</SelectItem><SelectItem value="W2">W2</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-sm mb-1.5 block">Pay Structure</Label>
                <Select value={createForm.pay_structure} onValueChange={v => setCreateForm(f => ({ ...f, pay_structure: v }))}><SelectTrigger data-testid="create-pay-structure"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="commission">Commission</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm mb-1.5 block">Hourly Rate ($)</Label><Input type="number" min="0" value={createForm.hourly_rate} onChange={e => setCreateForm(f => ({ ...f, hourly_rate: e.target.value }))} data-testid="create-hourly-rate" /></div>
              <div><Label className="text-sm mb-1.5 block">Commission (%)</Label><Input type="number" min="0" max="100" value={createForm.commission_rate} onChange={e => setCreateForm(f => ({ ...f, commission_rate: e.target.value }))} data-testid="create-commission-rate" /></div>
            </div>
            <Button className="w-full bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={createCleaner} disabled={saving} data-testid="save-new-cleaner-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Create Guru
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Module 10 Addendum — per-Guru task management */}
      <GuruTasksDialog
        open={!!tasksDialogGuru}
        onClose={() => setTasksDialogGuru(null)}
        guru={tasksDialogGuru}
      />
    </div>
  );
};

export default AdminCleanersPage;
